import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { uploadImageToServer } from '../utils/imageUpload';
import ImageUploadZone from '../components/ui/ImageUploadZone';
import { useToast } from '../components/ui/Toast';
import { useAuth } from '../components/auth/AuthContext';

export default function WrongAnswerNote() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const [selectedLevel, setSelectedLevel] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [capturedImage, setCapturedImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [error, setError] = useState(null);

  const levels = ['중학생', '고등학생', '대학생', '일반인'];
  const subjects = {
    '중학생': ['수학', '영어', '국어', '과학', '사회'],
    '고등학생': ['수학', '영어', '국어', '과학', '사회', '한국사'],
    '대학생': ['수학', '영어', '전공', '교양'],
    '일반인': ['외국어', '자격증', '기타']
  };

  const handleFileSelect = (file) => {
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (event) => {
      setCapturedImage(event.target.result);
    };
    reader.readAsDataURL(file);
    setError(null);
  };

  const handleClearImage = () => {
    setImageFile(null);
    setCapturedImage(null);
  };

  const handleAnalyze = async () => {
    if (!selectedLevel || !selectedSubject || !imageFile) {
      toast.warning('레벨, 과목, 이미지를 모두 선택해주세요.');
      return;
    }

    setAnalyzing(true);
    setError(null);

    try {
      const imageUrl = await uploadImageToServer(imageFile, {
        resize: true,
        maxDimension: 2048,
        ttl: 86400 * 30
      });

      const formData = new FormData();
      formData.append('image', imageFile);
      formData.append('level', selectedLevel);
      formData.append('subject', selectedSubject);

      const response = await fetch('/api/learning/analyze', {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || '분석 실패');
      }

      setAnalysisResult({
        ...result.data,
        imageUrl,
        level: selectedLevel,
        subject: selectedSubject
      });
      toast.success('분석이 완료되었습니다!');
    } catch (err) {
      console.error('Analysis error:', err);
      setError(err.message || '분석 중 오류가 발생했습니다.');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSave = async () => {
    if (!analysisResult) return;

    try {
      const response = await fetch('/api/learning/records', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: user.user_id,
          level: analysisResult.level,
          subject: analysisResult.subject,
          image_url: analysisResult.imageUrl,
          problem_text: analysisResult.problem_text,
          student_answer: analysisResult.student_answer,
          correct_answer: analysisResult.correct_answer,
          explanation: analysisResult.explanation,
          key_concept: analysisResult.key_concept,
          difficulty: analysisResult.difficulty
        })
      });

      const result = await response.json();

      if (result.success) {
        toast.success('오답노트에 저장되었습니다!');
        setCapturedImage(null);
        setImageFile(null);
        setAnalysisResult(null);
      } else {
        throw new Error(result.error);
      }
    } catch (err) {
      console.error('Save error:', err);
      toast.error('저장 실패: ' + err.message);
    }
  };

  const chipStyle = (isSelected, color) => ({
    padding: '0.65rem 1.25rem',
    border: 'none',
    borderRadius: 'var(--radius-full, 9999px)',
    background: isSelected ? color : 'var(--color-surface-dim, #f0f0f0)',
    color: isSelected ? 'white' : 'var(--color-text, #333)',
    cursor: 'pointer',
    fontWeight: isSelected ? 700 : 500,
    fontSize: 'var(--font-size-sm, 0.9rem)',
    transition: 'all var(--transition-fast, 150ms)',
    boxShadow: isSelected ? `0 2px 8px ${color}44` : 'none',
  });

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--gradient-accent)',
      padding: 'var(--space-xl)',
    }}>
      <div style={{
        maxWidth: 'var(--max-width, 800px)',
        margin: '0 auto',
        background: 'var(--color-surface, white)',
        borderRadius: 'var(--radius-xl, 1rem)',
        padding: 'var(--space-xl)',
        boxShadow: 'var(--shadow-lg)',
        animation: 'fadeInUp 0.4s ease forwards',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          marginBottom: 'var(--space-xl)',
        }}>
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: 'var(--color-text)',
            }}
          >
            ←
          </button>
          <h1 style={{
            fontSize: 'var(--font-size-2xl, 2rem)',
            color: 'var(--color-text, #333)',
            margin: 0,
          }}>
            📸 오답노트 등록
          </h1>
        </div>

        {/* Step 1: Level */}
        <div style={{ marginBottom: 'var(--space-xl)' }}>
          <label style={{
            display: 'block',
            marginBottom: 'var(--space-sm)',
            fontWeight: 700,
            color: 'var(--color-text)',
          }}>
            학생 레벨:
          </label>
          <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
            {levels.map(level => (
              <button
                key={level}
                onClick={() => {
                  setSelectedLevel(level);
                  setSelectedSubject('');
                }}
                style={chipStyle(selectedLevel === level, 'var(--color-accent, #667eea)')}
              >
                {level}
              </button>
            ))}
          </div>
        </div>

        {/* Step 2: Subject */}
        {selectedLevel && (
          <div style={{
            marginBottom: 'var(--space-xl)',
            animation: 'fadeInUp 0.3s ease forwards',
          }}>
            <label style={{
              display: 'block',
              marginBottom: 'var(--space-sm)',
              fontWeight: 700,
              color: 'var(--color-text)',
            }}>
              과목:
            </label>
            <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
              {subjects[selectedLevel].map(subject => (
                <button
                  key={subject}
                  onClick={() => setSelectedSubject(subject)}
                  style={chipStyle(selectedSubject === subject, '#764ba2')}
                >
                  {subject}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Image */}
        <div style={{ marginBottom: 'var(--space-xl)' }}>
          <label style={{
            display: 'block',
            marginBottom: 'var(--space-sm)',
            fontWeight: 700,
            color: 'var(--color-text)',
          }}>
            문제 사진:
          </label>
          <ImageUploadZone
            onFileSelect={handleFileSelect}
            imagePreview={capturedImage}
            onClear={handleClearImage}
            instruction="문제 사진을 촬영하거나 선택해주세요"
          />
        </div>

        {/* Step 4: Analyze button */}
        {capturedImage && selectedLevel && selectedSubject && !analysisResult && (
          <button
            onClick={handleAnalyze}
            disabled={analyzing}
            style={{
              width: '100%',
              padding: 'var(--space-md)',
              background: analyzing ? '#ccc' : 'var(--gradient-accent)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-md, 0.5rem)',
              fontSize: '1.1rem',
              fontWeight: 700,
              cursor: analyzing ? 'not-allowed' : 'pointer',
              marginBottom: 'var(--space-md)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
            }}
          >
            {analyzing ? (
              <>
                <span style={{ display: 'inline-block', animation: 'pulse 1.5s ease-in-out infinite' }}>🤖</span>
                AI 분석 중...
              </>
            ) : '🔍 AI 분석 시작'}
          </button>
        )}

        {/* Error */}
        {error && (
          <div style={{
            padding: 'var(--space-md)',
            background: 'var(--color-danger-bg, #fee)',
            border: '1px solid #f5c6cb',
            borderRadius: 'var(--radius-lg)',
            color: 'var(--color-danger-text, #c33)',
            marginBottom: 'var(--space-md)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.75rem',
            animation: 'fadeInUp 0.3s ease forwards',
          }}>
            <span style={{ flexShrink: 0 }}>⚠️</span>
            <div style={{ flex: 1, fontSize: 'var(--font-size-sm)' }}>{error}</div>
            <button
              onClick={() => setError(null)}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--color-danger)',
                cursor: 'pointer',
                fontSize: '1.2rem',
                padding: 0,
                flexShrink: 0,
              }}
            >
              ✕
            </button>
          </div>
        )}

        {/* Analysis result */}
        {analysisResult && (
          <div style={{
            padding: 'var(--space-lg)',
            background: 'var(--color-surface-dim, #f9f9f9)',
            borderRadius: 'var(--radius-lg)',
            border: '2px solid var(--color-accent, #667eea)',
            animation: 'fadeInUp 0.4s ease forwards',
          }}>
            <h2 style={{
              fontSize: '1.3rem',
              marginBottom: 'var(--space-md)',
              color: 'var(--color-accent, #667eea)',
            }}>
              ✅ 분석 완료
            </h2>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-md)',
            }}>
              {analysisResult.problem_text && (
                <ResultBox title="📝 문제" content={analysisResult.problem_text} />
              )}
              {analysisResult.student_answer && (
                <ResultBox title="✏️ 내 답" content={analysisResult.student_answer} bg="#ffe" />
              )}
              {analysisResult.correct_answer && (
                <ResultBox title="✓ 정답" content={analysisResult.correct_answer} bg="#efe" />
              )}
              {analysisResult.explanation && (
                <ResultBox title="💡 해설" content={analysisResult.explanation} />
              )}
              {analysisResult.key_concept && (
                <ResultBox title="🎯 핵심 개념" content={analysisResult.key_concept} bg="#eef" />
              )}
              {analysisResult.difficulty && (
                <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                  <strong>📊 난이도:</strong> {analysisResult.difficulty}
                </div>
              )}
            </div>

            <button
              onClick={handleSave}
              style={{
                width: '100%',
                padding: 'var(--space-md)',
                background: 'var(--color-success, #28a745)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontSize: '1.1rem',
                fontWeight: 700,
                cursor: 'pointer',
                marginTop: 'var(--space-lg)',
                transition: 'opacity var(--transition-fast)',
              }}
            >
              💾 오답노트에 저장
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ResultBox({ title, content, bg = 'white' }) {
  if (!content) return null;
  return (
    <div>
      <strong style={{ fontSize: 'var(--font-size-sm)' }}>{title}</strong>
      <div style={{
        padding: '0.75rem',
        background: bg,
        borderRadius: 'var(--radius-md)',
        marginTop: '0.35rem',
        whiteSpace: 'pre-wrap',
        fontSize: 'var(--font-size-sm)',
        lineHeight: 1.6,
      }}>
        {content}
      </div>
    </div>
  );
}
